import React, { useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import { Button } from './ui/button';
import { Shield, ArrowRight, LogOut, X, Building2 } from 'lucide-react';

const EMPLOYER_ROLES = ['company_admin', 'supervisor', 'admin'];

/**
 * EmployerRoute — role-aware route guard for employer-only modules.
 *
 * Not logged in       → redirect to /login (with returnUrl)
 * Employer role       → render children
 * Worker/passport role → show "Switch to Employer Account" modal
 */
export function EmployerRoute({ children, fallback }) {
  const { isAuthenticated, loading, user, logout } = useAuth();
  const location = useLocation();
  const [showModal, setShowModal] = useState(true);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#F8FAFC' }}>
        <div className="w-12 h-12 border-4 rounded-full animate-spin mx-auto" style={{ borderColor: '#1A3D7C', borderTopColor: 'transparent' }} />
      </div>
    );
  }

  // Not logged in → redirect to login with return URL
  if (!isAuthenticated) {
    // If a public fallback page is provided (e.g. onboarding placeholder), show it instead
    if (fallback) return fallback;
    // Store return URL in sessionStorage for the login page
    sessionStorage.setItem('redirectAfterLogin', location.pathname);
    return <Navigate to="/login" replace />;
  }

  // Employer role → allow access
  if (EMPLOYER_ROLES.includes(user?.role)) {
    return children;
  }

  // Worker role → show switch modal over the fallback or a blank page
  return (
    <>
      {fallback || (
        <div className="min-h-screen" style={{ backgroundColor: '#F8FAFC' }} />
      )}
      {showModal && (
        <SwitchAccountModal
          currentRole={user?.role}
          targetPath={location.pathname}
          onClose={() => setShowModal(false)}
          onLogout={logout}
        />
      )}
    </>
  );
}

function SwitchAccountModal({ currentRole, targetPath, onClose, onLogout }) {
  const roleLabel = currentRole === 'passport_only' ? 'Passport holder' : 'Worker';

  const handleSwitchAccount = () => {
    onLogout();
    // After logout, navigate will happen via AuthContext clearing state
    window.location.href = `/login?returnUrl=${encodeURIComponent(targetPath)}`;
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" data-testid="switch-account-modal">
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full mx-4 overflow-hidden">
        {/* Header */}
        <div className="px-6 pt-6 pb-4 flex items-start justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: '#FEF3C7' }}>
              <Shield className="w-5 h-5" style={{ color: '#D97706' }} />
            </div>
            <div>
              <h2 className="text-lg font-bold" style={{ color: '#2E3238' }}>Employer Access Required</h2>
              <p className="text-xs" style={{ color: '#94A3B8' }}>This module is for employer accounts</p>
            </div>
          </div>
          <button onClick={onClose} className="p-1 rounded-lg hover:bg-slate-100 transition-colors" data-testid="close-switch-modal">
            <X className="w-5 h-5 text-slate-400" />
          </button>
        </div>

        {/* Body */}
        <div className="px-6 pb-6">
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-5">
            <p className="text-sm" style={{ color: '#92400E' }}>
              You&apos;re currently signed in as a <strong>{roleLabel}</strong>. This section is only available to employer accounts (Company Admin or Supervisor).
            </p>
          </div>

          <p className="text-sm mb-5" style={{ color: '#64748B' }}>
            To access employer features, sign out and log in with your employer credentials. If you don&apos;t have an employer account, contact your organisation&apos;s admin.
          </p>

          <div className="space-y-2">
            <Button
              onClick={handleSwitchAccount}
              className="w-full h-11 rounded-lg text-sm font-semibold flex items-center justify-center gap-2"
              style={{ backgroundColor: '#1A3D7C', color: '#fff' }}
              data-testid="switch-to-employer-btn"
            >
              <LogOut className="w-4 h-4" />
              Sign Out & Switch to Employer
              <ArrowRight className="w-4 h-4" />
            </Button>
            <Button
              onClick={onClose}
              variant="outline"
              className="w-full h-11 rounded-lg text-sm font-medium"
              data-testid="stay-as-worker-btn"
            >
              <Building2 className="w-4 h-4 mr-2" />
              Stay as {roleLabel}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
